import { z } from "zod";
import {
  Box,
  Text,
  WidgetInteractive,
  WidgetSkeleton,
  compactNumber,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import { STAGE_TYPES } from "#app/lib/data";
import { stageTypeLabel } from "#app/lib/entity-visuals";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { resolveStoredTitle } from "../shared/derived-titles";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { categoryProjectsLabel, stageCategorySchema } from "./category";
import { useProjectsClosed } from "./use-projects-closed";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";

export const projectsClosedConfigSchema = z
  .looseObject({
    // One category, never a blend: an older row's `includeLost` flag is left
    // where it lies and reads as Completed, the only count it was honest about.
    category: stageCategorySchema.default("done"),
    compact: z.boolean().default(false),
    filters: widgetFiltersSchema.nullish(),
  });

export type ProjectsClosedConfig = z.infer<typeof projectsClosedConfigSchema>;

const GALLERY_LABEL = "Projects by category";
const DEFAULT_TITLE = categoryProjectsLabel("done");

/** Every title this family has ever written for itself — each category's
 *  form, the gallery card's name a placement stores, and the retired
 *  "Projects closed" a seeded board baked — so a Category change re-derives
 *  and a member's rename sticks. */
const GENERATED_TITLES = new Set<string>([
  ...STAGE_TYPES.map(categoryProjectsLabel),
  GALLERY_LABEL,
  "Projects closed",
]);

function ProjectsClosedRender({ h, config }: WidgetRenderContext<ProjectsClosedConfig>) {
  const { value, stageIds, loading, error } = useProjectsClosed(config.category, config.filters);
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="stat" />;
  }

  const canDrill = Boolean(drill && instance && stageIds.length > 0);
  const openData = () => {
    if (!drill || !instance || stageIds.length === 0) return;
    drill.openData(instance.instanceId, {
      kind: "dimension",
      dimension: "stage",
      ids: stageIds,
      label: stageTypeLabel(config.category) ?? config.category,
    });
  };

  // The wash bleeds `xs` back over the body's inset on the sides and the
  // bottom alike — the metric tile's step — so it keeps an 8px ring to the
  // card's frame on all three edges; the stack runs with no gap to match.
  return (
    <Box fill="flex" align="center" justify="center" marginXNegative="xs" marginBottomNegative="xs">
      <WidgetInteractive
        fill="cell"
        rounded="lg"
        align="center"
        justify="center"
        cursor={canDrill ? "pointer" : undefined}
        hoverBackground={canDrill}
        onClick={canDrill ? openData : undefined}
      >
        <Box align="center" gap="none">
          <Text size={h >= 3 ? "4xl" : "2xl"} weight="bold" truncate={1}>
            {config.compact ? compactNumber(value) : value.toLocaleString()}
          </Text>
          <Text size="sm" variant="muted" truncate={1}>
            {categoryProjectsLabel(config.category)}
          </Text>
        </Box>
      </WidgetInteractive>
    </Box>
  );
}

const projectsClosedSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      {
        key: "category",
        kind: "select",
        label: "Category",
        icon: PROPERTY_ICON.stage,
        options: STAGE_TYPES.map((type) => ({
          value: type,
          label: stageTypeLabel(type) ?? type,
        })),
      },
    ],
  },
  {
    title: "Display",
    fields: [
      { key: "compact", kind: "toggle", label: "Abbreviate large numbers", icon: "minimize" },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const projectsClosedDef: WidgetModule<ProjectsClosedConfig> = {
  type: "projects-closed",
  title: DEFAULT_TITLE,
  dynamicTitle: (config) =>
    resolveStoredTitle(
      config,
      (title) => GENERATED_TITLES.has(title),
      categoryProjectsLabel(config.category),
    ),
  icon: PROPERTY_ICON.stage,
  span: { minW: 2, minH: 3, maxW: 12, maxH: 4, defaultW: 3, defaultH: 3 },
  configSchema: projectsClosedConfigSchema,
  settings: projectsClosedSettings,
  headerActions: WidgetHeaderFilters,
  bodyDraggable: true,
  catalog: [
    {
      key: "projects-closed.default",
      label: GALLERY_LABEL,
      description:
        "Counts the Projects in scope in one stage category — Not started, In progress, Completed or Canceled.",
      section: "projects" satisfies WidgetSection,
      tags: ["projects"] satisfies readonly WidgetTag[],
      defaultConfig: { category: "done" },
      defaultW: 3,
    },
  ],
  render: ProjectsClosedRender,
};
